import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContent } from "@/i18n";
import { scrollToHash } from "@/hooks/useLenis";
import { Logo } from "./Logo";
import { EASE } from "./Reveal";

const LINKS = [
  { key: "home", hash: "#home" },
  { key: "about", hash: "#about" },
  { key: "practice", hash: "#practice" },
  { key: "advocates", hash: "#advocates" },
  { key: "careers", to: "/careers" },
  { key: "contact", hash: "#contact" },
];

export const Navbar = () => {
  const { t } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open ? "border-gold/20 bg-navy-900/85 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl" : "border-transparent bg-navy-900/40 backdrop-blur-md"
      }`}
    >
      <nav aria-label="Primary" className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 md:px-12">
        <a href="#home" onClick={go("#home")} data-testid="nav-logo" aria-label="Nexprime Law Chambers — home" className="focus-ring">
          <Logo compact={scrolled} />
        </a>
        <ul className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <li key={l.key}>
              {l.to ? (
                <Link
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-${l.key}`}
                  className="nav-link focus-ring font-sans text-[13px] tracking-[0.12em] text-ink transition-colors duration-300 hover:text-gold-light"
                >
                  {t.nav[l.key]}
                </Link>
              ) : (
                <a
                  href={l.hash}
                  onClick={go(l.hash)}
                  data-testid={`nav-link-${l.key}`}
                  className="nav-link focus-ring font-sans text-[13px] tracking-[0.12em] text-ink transition-colors duration-300 hover:text-gold-light"
                >
                  {t.nav[l.key]}
                </a>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          data-testid="nav-menu-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? t.nav.close : t.nav.menu}
          onClick={() => setOpen((v) => !v)}
          className="focus-ring inline-flex h-11 w-11 items-center justify-center text-gold md:hidden"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="border-t border-gold/15 bg-navy-900/95 backdrop-blur-xl md:hidden"
          >
            <ul className="flex flex-col px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
                  className="border-b border-white/5 last:border-none"
                >
                  {l.to ? (
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${l.key}`}
                      className="focus-ring block py-4 font-serif text-2xl text-paper transition-colors duration-300 hover:text-gold-light"
                    >
                      {t.nav[l.key]}
                    </Link>
                  ) : (
                    <a
                      href={l.hash}
                      onClick={go(l.hash)}
                      data-testid={`mobile-nav-link-${l.key}`}
                      className="focus-ring block py-4 font-serif text-2xl text-paper transition-colors duration-300 hover:text-gold-light"
                    >
                      {t.nav[l.key]}
                    </a>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
